import { useEffect, useState } from "react";
import Ico from "../../components/Ico";
import { Supplier } from "../../suppliers";

interface NpAutoMatchedProps {
  /** 配對成功時要揭示的瓦斯行；noMatch（查無）時不需要。 */
  supplier?: Supplier;
  /** true=查無合作瓦斯行：只播「配對中」雷達，不揭示瓦斯行，短暫後 onDone（接等待頁 pending）。 */
  noMatch?: boolean;
  /** 過場結束後自動繼續（配對到→完成加入；查無→等待頁） */
  onDone: () => void;
}

/**
 * 傳單・無指定瓦斯行：一路都沒比對到 → 系統依地址自動配對「最近的合作瓦斯行」。
 * 純過場效果：先「配對中」雷達脈動，短暫後切成「配對成功」揭示瓦斯行，再自動跳轉。
 * noMatch（查無）：只播「配對中」雷達，不揭示，短暫後 onDone 進等待頁——讓「查無」也有配對過場、不突兀。
 * 不需按確認、也不提供換一家。
 */
const NpAutoMatched = ({ supplier, noMatch, onDone }: NpAutoMatchedProps) => {
  const [matched, setMatched] = useState(false); // false=配對中；true=已配對（揭示瓦斯行）

  useEffect(() => {
    if (noMatch) {
      const done = setTimeout(onDone, 1500); // 只播「配對中」雷達 ~1.5s → 等待頁
      return () => clearTimeout(done);
    }
    const reveal = setTimeout(() => setMatched(true), 1100); // 配對中 → 配對成功
    const done = setTimeout(onDone, 2600); // 全部結束 → 繼續
    return () => {
      clearTimeout(reveal);
      clearTimeout(done);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="npam-root h-full w-full flex flex-col items-center justify-center px-9 text-center overflow-hidden">
      <style>{`
        @keyframes npamRing { 0% { transform: scale(0.55); opacity: 0.55; } 80% { opacity: 0; } 100% { transform: scale(2.4); opacity: 0; } }
        @keyframes npamPop { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.12); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes npamCheckPop { 0% { transform: scale(0); } 60% { transform: scale(1.25); } 100% { transform: scale(1); } }
        @keyframes npamRise { 0% { transform: translateY(10px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes npamDot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
        @keyframes npamBarSweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(320%); } }
        .npam-ring { position: absolute; inset: 0; border-radius: 9999px; border: 2px solid #60A5FA; animation: npamRing 1.8s ease-out infinite; }
        .npam-icon { animation: npamPop 0.55s cubic-bezier(0.2,0.9,0.3,1) both; }
        .npam-check { animation: npamCheckPop 0.45s cubic-bezier(0.2,1.4,0.4,1) both; }
        .npam-rise { animation: npamRise 0.5s ease-out both; }
        .npam-dot { animation: npamDot 1.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .npam-ring, .npam-icon, .npam-check, .npam-rise, .npam-dot { animation: none !important; }
        }
      `}</style>

      {/* 圖示 + 雷達脈動 / 成功勾 */}
      <div className="relative w-[112px] h-[112px] flex items-center justify-center mb-6">
        {!matched && (
          <>
            <span className="npam-ring" style={{ animationDelay: "0s" }} />
            <span className="npam-ring" style={{ animationDelay: "0.6s" }} />
            <span className="npam-ring" style={{ animationDelay: "1.2s" }} />
          </>
        )}
        <div
          className="npam-icon relative w-[76px] h-[76px] rounded-[22px] flex items-center justify-center transition-colors duration-500"
          style={{
            background: matched ? "linear-gradient(135deg,#22C55E,#16A34A)" : "linear-gradient(135deg,#3B82F6,#2196f3)",
            boxShadow: matched ? "0 12px 26px -10px rgba(22,163,74,0.65)" : "0 12px 26px -10px rgba(33,150,243,0.65)",
          }}
        >
          <Ico name="store" c="#fff" s={38} />
          {matched && (
            <div className="npam-check absolute -bottom-1.5 -right-1.5 w-[30px] h-[30px] rounded-full bg-white flex items-center justify-center shadow-[0_3px_8px_rgba(0,0,0,0.18)]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#16A34A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {!matched ? (
        <>
          <div className="npam-rise text-[19px] font-extrabold text-[#1E293B]">正在為您配對瓦斯行</div>
          <div className="npam-rise text-[13.5px] text-[#94A3B8] mt-2" style={{ animationDelay: "0.1s" }}>
            依您的地址尋找最近的合作瓦斯行
          </div>
          <div className="flex items-center gap-1.5 mt-5">
            <span className="npam-dot w-[7px] h-[7px] rounded-full bg-[#3B82F6]" style={{ animationDelay: "0s" }} />
            <span className="npam-dot w-[7px] h-[7px] rounded-full bg-[#3B82F6]" style={{ animationDelay: "0.2s" }} />
            <span className="npam-dot w-[7px] h-[7px] rounded-full bg-[#3B82F6]" style={{ animationDelay: "0.4s" }} />
          </div>
        </>
      ) : (
        <>
          <div className="npam-rise text-[14px] text-[#16A34A] font-bold tracking-wide">已為您配對合作瓦斯行</div>
          <div className="npam-rise text-[23px] font-extrabold text-[#1565C0] leading-tight mt-1.5" style={{ animationDelay: "0.06s" }}>
            {supplier?.name}
          </div>
          <div className="npam-rise text-[13px] text-[#94A3B8] mt-2" style={{ animationDelay: "0.14s" }}>
            {supplier?.area}
          </div>
          {/* 帶入進度掃光條 */}
          <div className="npam-rise relative w-[150px] h-[4px] rounded-full bg-[#E8EEF5] overflow-hidden mt-6" style={{ animationDelay: "0.2s" }}>
            <span
              className="absolute top-0 left-0 h-full w-1/3 rounded-full bg-[#22C55E]"
              style={{ animation: "npamBarSweep 1.1s ease-in-out infinite" }}
            />
          </div>
          <div className="npam-rise text-[12.5px] text-[#94A3B8] mt-3" style={{ animationDelay: "0.26s" }}>
            正在為您帶入，請稍候…
          </div>
        </>
      )}
    </div>
  );
};

export default NpAutoMatched;
